import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import { motion, AnimatePresence } from "framer-motion";
import { DateTime } from "luxon";
import expertDefaultImg from "../../images/expert-default.png";
import Application from "./Application";
import styles from "./MyProjectPage.module.css";

export default function ProjectsPage({ expertID, showStickyHeader }) {
  const { API_URL } = useContext(UrlContext);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const [publishedProjects, setPublishedProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showApplication, setShowApplication] = useState(false);

  useEffect(() => {
    fetchProjects(page);
  }, [page]);

  const fetchProjects = async (pageNumber) => {
    const tokenObj = localStorage.getItem("eph-token");
    const token = JSON.parse(tokenObj).value || localStorage.getItem("AccessToken");
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}get-published-projects?page=${pageNumber}&per_page=10&expertID=${expertID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        Swal.fire({
          title: "Something Went Wrong!",
          icon: "error",
          confirmButtonColor: "#365561"
        });
      }

      const data = await response.json();

      if (data.status === "success") {
        setPublishedProjects(data.projects);
        setTotalPages(data.pages);
      } else {
        Swal.fire({
          title: "Something Went Wrong!",
          icon: "error",
          confirmButtonColor: "#365561"
        });
      }
    } catch (error) {
      Swal.fire({ title: "Something Went Wrong!", icon: "error", confirmButtonColor: "#365561" });
    } finally {
      setLoading(false);
    }
  };

  const getPaginationNumbers = (page, totalPages, maxButtons) => {
    const pages = [];

    if (totalPages <= maxButtons + 1) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const leftSide = Math.floor(maxButtons / 2);
      const rightSide = maxButtons - leftSide - 1;

      let start = Math.max(2, page - leftSide);
      let end = Math.min(totalPages - 1, page + rightSide);

      if (page <= leftSide) {
        start = 2;
        end = maxButtons;
      }

      if (page + rightSide >= totalPages) {
        start = totalPages - maxButtons + 1;
        end = totalPages - 1;
      }

      pages.push(1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    const updateView = () => setIsMobile(window.innerWidth < 640);
    updateView();
    window.addEventListener("resize", updateView);
    return () => window.removeEventListener("resize", updateView);
  }, []);

  const paginationNumbers = getPaginationNumbers(
    page,
    totalPages,
    isMobile ? 3 : 10
  );

  const formatRelativeDate = (dateStr) => {
    const created = DateTime.fromRFC2822(dateStr);
    const now = DateTime.local();

    if (!created.isValid) return "Unknown date";

    if (created.hasSame(now, "day")) return "Today";

    const diffInDays = Math.round(now.diff(created, "days").days);

    if (diffInDays >= 365) {
      const years = Math.floor(diffInDays / 365);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    } else if (diffInDays >= 30) {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    } else {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }
  };

  const formatStartDate = (dateStr) => {
    const date = DateTime.fromRFC2822(dateStr); // or .fromISO() if your format changes
    if (!date.isValid) return "Start date unknown";

    return `Starts from ${date.toFormat("LLLL d, yyyy")}`;
  };

  const getEmploymentMode = (type) => {
    if (!type) return "N/A";

    const lower = type.toLowerCase();

    const hasRemote = lower.includes("remote");
    const hasOnsite = lower.includes("onsite");

    if (hasRemote && hasOnsite) return "Hybrid";
    if (hasRemote) return "Remote";
    if (hasOnsite) return "On-Site";

    return "N/A";
  };

  const formatSkills = (skillsString) => {
    if (!skillsString) return "N/A";
    return skillsString
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean) // removes empty strings
      .join(", ");
  };

  const contactSupporterHandler = (supporter, project) => {
    if (supporter && project) {
      window.open(
        `mailto:${supporter.email}?subject=${project.name} - ${project.zoho_id}`,
        "__blank"
      );
    } else {
      Swal.fire({
        title: "Error",
        text: "Please try again later!",
        icon: "error",
        confirmButtonColor: "#365561"
      });
    }
  };

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const updateProjectStatus = (projectId, newStatus) => {
    setPublishedProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId
          ? { ...project, requestStatus: newStatus }
          : project
      )
    );

    if (selectedProject?.id === projectId) {
      setSelectedProject((prev) => ({ ...prev, requestStatus: newStatus }));
    }
  };

  return (
    <>
      <section className="col-span-4 space-y-6 relative min-h-[300px]">
        <motion.div className="flex flex-col lg:flex-row gap-4 transition-all duration-500">
          {/* Project List */}
          <motion.div
            // layout
            className={`transition-all duration-500 ${
              selectedProject ? "hidden lg:block lg:w-1/2" : "block lg:w-full"
            }`}
          >
            <div className="space-y-3 min-h-[120px] flex flex-col">
              {/* Overlay Spinner */}
              {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/30 rounded-xl">
                  <svg
                    className="animate-spin h-20 w-20 text-gray-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#365561"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="#365561"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </div>
              )}
              {/* Project List or Empty State */}
              {publishedProjects.length === 0 && !loading ? (
                <p className="text-gray-500 text-center text-sm">
                  No projects available now!
                </p>
              ) : (
                publishedProjects.map((project, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedProject(project)}
                    className={`relative group rounded-xl shadow p-3 cursor-pointer transition-all duration-200 ${
                      project.id === selectedProject?.id
                        ? "bg-[#eaf2f8] border-l-4 border-[#365561]"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <h2 className="text-xl font-bold mb-1">{project.name}</h2>
                    <p className="text-sm text-gray-500">
                      {project.country
                        ? `${project.country}`
                        : project.region
                        ? `${project.region}`
                        : null}{" "}
                      {project.employmentType
                        ? ` • ${getEmploymentMode(project.employmentType)}`
                        : null}{" "}
                      •{" "}
                      {project.projStartDate
                        ? formatStartDate(project.projStartDate)
                        : null}
                    </p>
                    <p className="text-sm text-gray-500">
                      {project.languagesNames.length > 0
                        ? project.languagesNames.join(", ")
                        : "No language mentioned"}{" "}
                      {project.projectDuration
                        ? ` • ${project.projectDuration}`
                        : ` • No duration mentioned`}
                    </p>
                    <p
                      className="text-xs text-gray-400 mt-1 overflow-hidden"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {stripHtml(
                        project.description || "No description available."
                      )}
                    </p>
                    <div className="mt-3 flex justify-between items-center text-xs text-gray-600 flex-wrap gap-y-1">
                      {/* Left side: Contact EPH button */}
                      <button
                        className="text-xs font-semibold text-gray-500 focus:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          contactSupporterHandler(project.supporter[0], project);
                        }}
                      >
                        ✉️ Contact EPH
                      </button>

                      {/* Right side: Deadline and Published Date */}
                      <div className="flex items-center gap-4">
                        <p className="font-semibold">
                          🕒 Deadline:{" "}
                          {project.projDeadline
                            ? DateTime.fromRFC2822(project.projDeadline).toFormat(
                                "MMM dd, yyyy"
                              )
                            : "N/A"}
                        </p>
                        <p>
                          🗓️ Published:{" "}
                          {project.date_created
                            ? DateTime.fromRFC2822(project.date_created).toFormat(
                                "MMM dd, yyyy"
                              )
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    {project.id === selectedProject?.id ? null : (
                      <div className="absolute top-0 right-0 z-10 group-hover:opacity-100 opacity-0 transition-opacity duration-300 pointer-events-none">
                        <div
                          className="relative w-[75px] h-[75px] bg-[#365561]"
                          style={{
                            clipPath: "polygon(100% 0, 0 0, 100% 100%)",
                            borderTopRightRadius: "0.75rem",
                          }}
                        >
                          {/* Text layer on top of triangle */}
                          <div className="absolute top-0 left-0 w-full h-full flex items-start justify-end pr-1 pt-1 pointer-events-none">
                            <p className="text-[10px] text-white font-semibold text-right leading-tight">
                              Click to see
                              <br />
                              more...
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {/* {publishedProjects.length > 10 && ( */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-wrap items-center gap-[6px]">
                <div className="mt-4 flex flex-wrap items-center gap-[6px]">
                  <div className="relative group">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === 1
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="First page"
                    >
                      <i className="fa-solid fa-angles-left" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      First Page
                    </span>
                  </div>

                  <div className="relative group">
                    <button
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === 1
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Previous page"
                    >
                      <i className="fa-solid fa-angle-left" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Previous Page
                    </span>
                  </div>

                  {/* Page Numbers */}
                  {paginationNumbers.map((p, i) => (
                    <button
                      key={i}
                      disabled={p === "..."}
                      onClick={() => typeof p === "number" && setPage(p)}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === p
                          ? "bg-[#365561] text-white font-semibold"
                          : "bg-white text-black"
                      } ${p === "..." && "cursor-default opacity-60"}`}
                    >
                      {p === "..." ? "…" : p}
                    </button>
                  ))}

                  <div className="relative group">
                    <button
                      onClick={() =>
                        setPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={page === totalPages}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === totalPages
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Next page"
                    >
                      <i className="fa-solid fa-angle-right" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Next Page
                    </span>
                  </div>

                  <div className="relative group">
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === totalPages
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Last page"
                    >
                      <i className="fa-solid fa-angles-right" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Last Page
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Project Detail */}
          <AnimatePresence mode="wait">
            {selectedProject && (
              <motion.div
                key="detail-panel"
                layout
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                // className={`w-full lg:w-1/2 h-fit bg-white p-6 rounded-xl shadow-md relative`}
                className={`
                  w-full lg:w-1/2 
                  lg:sticky ${
                    showStickyHeader ? "lg:top-[80px]" : "lg:top-[100px]"
                  }
                  lg:self-start
                  lg:max-h-[calc(100vh-160px)]
                  overflow-y-hidden hover:overflow-y-auto 
                  bg-white p-6 rounded-xl shadow-md 
                  scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
                  ${styles.scrollbarOverlay}
                `}
              >
                <div className="flex justify-end mb-2">
                  <button
                    className="top-5 right-6 text-gray-500 hover:text-black hover:bg-gray-200 py-1 px-2.5 rounded-full transition focus:outline-none"
                    onClick={() => setSelectedProject(null)}
                    title="Close"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                <h2 className="text-xl font-bold">{selectedProject.name}</h2>
                <p className="text-sm text-gray-500 mt-3">
                  {selectedProject.country
                    ? selectedProject.country
                    : selectedProject.region
                    ? selectedProject.region
                    : "N/A"}
                  {" • "}
                  {formatRelativeDate(selectedProject.date_created)}
                  {selectedProject.projStartDate
                    ? ` •  ${formatStartDate(selectedProject.projStartDate)}`
                    : null}
                </p>
                <div className="mt-3 text-sm text-gray-700">
                  <p>
                    <span className="mr-2">💼</span>
                    {getEmploymentMode(selectedProject.employmentType)}{" "}
                    {selectedProject.projectDuration
                      ? ` •  ${selectedProject.projectDuration}`
                      : null}
                  </p>
                  <p>
                    <span className="mr-2">🏭</span>
                    {selectedProject.industryPref === "Yes"
                      ? selectedProject.industry || "N/A"
                      : "N/A"}
                  </p>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5">🛠️</span>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {formatSkills(selectedProject.skills)}
                    </p>
                  </div>
                </div>
                <div className="mt-3 space-x-2">
                  <button
                    className={`px-4 py-2 rounded-full text-sm focus:outline-none transition ${
                      selectedProject.requestStatus === null || selectedProject.requestStatus === "Draft"
                        ? "bg-[#365561] text-white hover:bg-[#2c444f] cursor-pointer"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                    onClick={ () => setShowApplication(true)}
                    disabled={selectedProject.requestStatus !== null && selectedProject.requestStatus !== "Draft"}
                  >
                    {selectedProject.requestStatus === null
                      ? "Apply Now"
                      : selectedProject.requestStatus === "Draft"
                      ? "Resume application"
                      : selectedProject.requestStatus === "Approved"
                      ? "Approved"
                      : selectedProject.requestStatus === "Rejected"
                      ? "Declined"
                      : "Reviewing"}
                  </button>
                </div>

                {/* <div className="mt-4 p-3 flex flex-col gap-4 border border-gray-300 rounded-xl relative overflow-hidden">
                  {selectedProject.requestStatus === null && (
                    <p className="font-semibold text-gray-700">
                      Apply to see contact person
                    </p>
                  )}
                  {selectedProject.requestStatus && selectedProject.requestStatus !== "Approved" && (
                    <p className="font-semibold text-gray-700">
                      Contact info available upon approval
                    </p>
                  )}

                  <div className="relative">
                    <div
                      className={`
                        flex flex-col gap-4 
                        ${selectedProject.requestStatus === null
                          ? 'blur-md pointer-events-none select-none'
                          : selectedProject.requestStatus !== "Approved"
                          ? 'blur-sm pointer-events-none select-none'
                          : ''}
                      `}
                      aria-hidden={selectedProject.requestStatus !== "Approved"}
                    >
                      {Array.isArray(selectedProject.supporter) &&
                        selectedProject.supporter.map((supporter, index) => (
                          <div key={index} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                              {selectedProject.requestStatus === "Approved" ? (
                                <>
                                  <img
                                    src={
                                      supporter.profilePicture?.includes(mediaLink)
                                        ? supporter.profilePicture
                                        : `${databaseLink}${supporter.profilePicture}`
                                    }
                                    onError={(e) => (e.target.src = expertDefaultImg)}
                                    className="w-10 h-10 rounded-full object-cover border shadow-sm"
                                    alt="Profile"
                                  />
                                  <div className="flex flex-col justify-center">
                                    <p className="font-semibold text-gray-700 leading-none">
                                      {supporter.name}
                                    </p>
                                    <p className="text-sm text-gray-500 leading-tight">
                                      {supporter.designation}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <img
                                    src={ expertDefaultImg }
                                    className="w-10 h-10 rounded-full object-cover border shadow-sm"
                                  />
                                  <div className="flex flex-col justify-center">
                                    <p className="font-semibold text-gray-700 leading-none">
                                      {'Your contact person'}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            <button
                              className="flex items-center border border-gray-400 px-3 py-1 text-sm text-gray-700 rounded-full hover:bg-gray-100 transition focus:outline-none"
                              onClick={() => {
                                if (selectedProject.requestStatus === "Approved") {
                                  contactSupporterHandler(supporter, selectedProject);
                                }
                              }}
                              disabled={selectedProject.requestStatus !== "Approved"}
                            >
                              <i className="mr-2 fa-solid fa-paper-plane"></i>
                              Message
                            </button>
                          </div>
                        ))}
                    </div>

                    {(selectedProject.requestStatus === null ||
                      selectedProject.requestStatus !== "Approved") && (
                      <div className="absolute inset-0 bg-white opacity-10 pointer-events-none" />
                    )}
                  </div>
                </div> */}

                <p className="mt-3 text-lg font-semibold">About the project</p>
                <div
                  className="mt-3 text-sm text-gray-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_h3]:text-lg [&_h3]:font-semibold"
                  dangerouslySetInnerHTML={{
                    __html:
                      selectedProject.description ||
                      "<p>No description available.</p>",
                  }}
                ></div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>
      
      {showApplication && (
        <Application
          project={selectedProject}
          selfApplied={true}
          expertID={expertID}
          updateProjectStatus={updateProjectStatus}
          onClose={() => setShowApplication(false)}
          
        />
      )}
    </>
  );
}
